import api from "@/utils/api";
import { API_ENDPOINTS, ApiResponse } from "@/constants/api";
import logger from "@/utils/logger";

export interface ContentItem {
    url: string;
    title: string;
    description: string;
    date: string;
}

interface CachedContents {
    data: ContentItem[];
    timestamp: number;
}

const CACHE_KEY = "cached_contents";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24시간(하루) 유지

/**
 * 콘텐츠 목록을 가져옵니다. 캐시된 데이터가 있으면 캐시를 사용합니다.
 * @returns 콘텐츠 목록 데이터
 */
export const fetchContents = async (): Promise<ContentItem[] | null> => {
    // 로컬 스토리지에서 캐시 확인
    const cachedData = localStorage.getItem(CACHE_KEY);

    if (cachedData) {
        const parsedCache = JSON.parse(cachedData) as CachedContents;
        const now = Date.now();

        // 캐시가 유효한지 확인 (24시간 이내)
        if (now - parsedCache.timestamp < CACHE_DURATION) {
            logger.log("캐시된 콘텐츠 데이터 사용");
            return parsedCache.data;
        }

        // 캐시 만료되었으면 삭제
        localStorage.removeItem(CACHE_KEY);
    }

    // 캐시가 없거나 만료된 경우 API 호출
    try {
        const response = await api.get<ApiResponse<ContentItem[]>>(API_ENDPOINTS.CONTENTS);

        if (response.data.success && response.data.data) {
            // 결과를 캐시에 저장
            const cacheData: CachedContents = {
                data: response.data.data,
                timestamp: Date.now()
            };

            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
            return response.data.data;
        }
        return null;
    } catch (error: any) {
        // 타임아웃이나 서버 에러 시 캐시된 데이터가 있으면 그것을 반환
        const oldCachedData = localStorage.getItem(CACHE_KEY);
        if (oldCachedData) {
            logger.log("API 요청 실패, 만료된 캐시 데이터 사용");
            try {
                const parsedOldCache = JSON.parse(oldCachedData) as CachedContents;
                return parsedOldCache.data;
            } catch (cacheError) {
                logger.error("캐시 데이터 파싱 실패:", cacheError);
            }
        }

        // 오류 메시지에 따른 처리
        if (error.code === "ECONNABORTED" && error.message?.includes("timeout")) {
            logger.error("콘텐츠 로드 타임아웃:", error);
        } else {
            logger.error("콘텐츠 목록 조회 실패:", error);
        }

        return null;
    }
};
